import { PERMISSIONS } from '@base/shared';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../auth.tsx';
import { PasswordChangeForm } from '../components/PasswordForm.tsx';
import { Field, PageHeader, Panel, Select } from '../components/ui.tsx';
import { LANGUAGES, localized } from '../i18n/index.ts';
import { ReadingSection } from './account-reading.tsx';

const LABELS = new Map(PERMISSIONS.map((p) => [p.code as string, p.label]));

/**
 * Configurações do usuário: abre pelo nome da pessoa no rodapé da lateral. Os dados dela, o idioma
 * das telas e o que ela pode fazer no sistema (antes "Meus acessos").
 */
export function AccountPage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  if (!me) return null;
  return (
    <div className="flex max-w-3xl flex-col gap-6">
      <PageHeader title={t('account.title')} description={t('account.description')} />
      <Panel title={t('account.profile')}>
        <dl className="grid gap-3 text-sm sm:grid-cols-2">
          <div className="flex flex-col gap-0.5">
            <dt className="text-muted">{t('account.name')}</dt>
            <dd className="font-medium">{me.user.displayName}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-muted">{t('account.email')}</dt>
            <dd className="break-all">{me.user.email}</dd>
          </div>
        </dl>
      </Panel>
      <Panel title={t('account.preferences')}>
        <Field label={t('common.language')} hint={t('account.languageHint')} className="max-w-xs">
          <Select
            id="language"
            value={i18n.language}
            onChange={(e) => void i18n.changeLanguage(e.target.value)}
          >
            {LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.label}
              </option>
            ))}
          </Select>
        </Field>
      </Panel>
      <ReadingSection />
      <Panel title={t('password.title')}>
        <p className="mb-4 text-sm text-muted">{t('password.hint')}</p>
        <PasswordChangeForm />
      </Panel>
      {/* Meus acessos fica recolhido: abre só quando a pessoa quer conferir o que pode fazer. */}
      <details className="group rounded-lg border border-rule bg-sheet shadow-sm">
        <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-2 px-5 py-3 text-sm font-medium text-ink">
          {t('account.showAccess')}
          <ChevronDown
            size={16}
            strokeWidth={1.75}
            aria-hidden
            className="text-muted transition group-open:rotate-180"
          />
        </summary>
        <div className="flex flex-col gap-3 border-t border-rule px-5 py-4">
          <p className="text-sm text-muted">{t('account.accessHint')}</p>
          {me.permissions.length === 0 ? (
            <p className="text-sm text-muted">{t('account.accessEmpty')}</p>
          ) : (
            <ul className="flex flex-col gap-1 text-sm">
              {me.permissions.map((p) => (
                <li key={p}>{localized(LABELS.get(p)) || p}</li>
              ))}
            </ul>
          )}
        </div>
      </details>
    </div>
  );
}
